import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataFeedRowData } from '../../../types/generatedTypes';
import { useDataFeedWizardStore } from '../../../store/dataFeedWizard/dataFeedWizardStore';
import Actions from '../../../store/Actions';

interface UseProcessedStatusResult {
  mergedRows: DataFeedRowData[];
  processedRows: DataFeedRowData[];
  processedFileId: string | null;
  templateId: string | null;
  reload: () => Promise<void>;
  isLoading: boolean;
  setProcessedRows: React.Dispatch<React.SetStateAction<DataFeedRowData[]>>;
}

export const useProcessedStatus = (
  fileId: string | null,
  rawRows: DataFeedRowData[]
): UseProcessedStatusResult => {
  const { uploadedRowsCount, setTemplateId } = useDataFeedWizardStore();
  const [processedRows, setProcessedRows] = useState<DataFeedRowData[]>([]);
  const [processedFileId, setProcessedFileId] = useState<string | null>(null);
  const [templateId, setLocalTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadProcessedData = useCallback(async () => {
    if (!fileId) {
      setProcessedRows([]);
      setProcessedFileId(null);
      setLocalTemplateId(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await Actions.getProcessedFileData(fileId);
      setProcessedFileId(response.processed_file_id || null);

      if (response.template_id) {
        setLocalTemplateId(response.template_id);
        setTemplateId(response.template_id);
      }

      const rowsWithId = (response.processed_rows || []).map(
        (row: DataFeedRowData, index: number) => ({
          ...row,
          id: `processed_${index}`,
          _processed_index: index,
        })
      );
      setProcessedRows(rowsWithId);
    } catch (error) {
      console.error('Failed to load processed data:', error);
      setProcessedRows([]);
      setProcessedFileId(null);
    } finally {
      setIsLoading(false);
    }
  }, [fileId, setTemplateId]);

  // Reload processed data when fileId changes or when uploads occur (uploadedRowsCount changes)
  useEffect(() => {
    loadProcessedData();
  }, [loadProcessedData, uploadedRowsCount]);

  // Merge upload_status and instrument type from processedRows to rawRows by _row_index
  const mergedRows = useMemo(() => {
    if (rawRows.length === 0) return [];

    const statusByIndex = new Map<number, DataFeedRowData['upload_status']>();
    const instrumentTypeByIndex = new Map<number, string>();

    processedRows.forEach(row => {
      const rowIndex = row._row_index as number | undefined;
      if (rowIndex !== undefined) {
        if (row.upload_status) {
          statusByIndex.set(rowIndex, row.upload_status);
        }
        const instrumentType = row._instrument_type ?? row.instrumentType;
        if (instrumentType) {
          instrumentTypeByIndex.set(rowIndex, String(instrumentType));
        }
      }
    });

    return rawRows.map(row => {
      const rowIndex = row._row_index as number;
      const instrumentType = instrumentTypeByIndex.get(rowIndex);
      return {
        ...row,
        upload_status:
          statusByIndex.get(rowIndex) ?? row.upload_status,
        ...(instrumentType
          ? { _instrument_type: instrumentType, instrumentType }
          : {}),
      };
    });
  }, [rawRows, processedRows]);

  return {
    mergedRows,
    processedRows,
    processedFileId,
    templateId,
    reload: loadProcessedData,
    isLoading,
    setProcessedRows,
  };
};
